"use client";
import React from "react";
import { Box, Container, Typography, Button } from "@mui/material";
import NextLink from "next/link";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ArrowDownwardRoundedIcon from "@mui/icons-material/ArrowDownwardRounded";
import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded";
import { useTranslation } from "react-i18next";

const SAGE_PINK = "#E0457B";
const arrowSx = {
  fontSize: 20,
  color: "#C2B4E8",
  stroke: "#C2B4E8",
  strokeWidth: 1.1,
};
const StepCard = ({ number, title, variant = "default", isArabic }) => {
  const styles = {
    default: {
      bg: "#FFFFFF",
      border: "1px solid #F3E4EC",
      numColor: "#8A88A3",
      titleColor: "#14122B",
    },
    pink: {
      bg: SAGE_PINK,
      border: `1px solid ${SAGE_PINK}`,
      numColor: "rgba(255,255,255,0.75)",
      titleColor: "#FFFFFF",
    },
    dark: {
      bg: "#14122B",
      border: "1px solid #14122B",
      numColor: "rgba(255,255,255,0.65)",
      titleColor: "#FFFFFF",
    },
  }[variant];

  return (
    <Box
      sx={{
        backgroundColor: styles.bg,
        border: styles.border,
        borderRadius: "12px",
        px: { xs: 2.5, md: 3 },
        py: { xs: 2, md: 2.75 },
        textAlign: isArabic ? "right" : "left",
        boxShadow: "0 2px 12px rgba(229,72,127,0.05)",
        height: "100%",
      }}
    >
      <Typography
        sx={{
          fontSize: "10px",
          fontWeight: 500,
          letterSpacing: "1px",
          color: styles.numColor,
          fontFamily: "var(--font-outfit)",
          mb: 0.5,
        }}
      >
        {number}
      </Typography>
      <Typography
        sx={{
          fontSize: { xs: "15px", md: "16px" },
          fontWeight: 700,
          color: styles.titleColor,
          fontFamily: "var(--font-plusjakarta)",
        }}
      >
        {title}
      </Typography>
    </Box>
  );
};

const Center = ({ children, sx }) => (
  <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", ...sx }}>
    {children}
  </Box>
);

const SageLoopSection = () => {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";

  const raw = t("sageLoop.steps", { returnObjects: true });
  const steps = Array.isArray(raw) ? raw : [];
  const num = (i) => `0${i + 1}`;
  const variantFor = (i) => (i === 2 ? "dark" : i === 3 ? "pink" : "default");
  const flip = { transform: isArabic ? "scaleX(-1)" : "none" };

  return (
    <Box
      component="section"
      sx={{
        py: { xs: 6, sm: 8, md: 10 },
        backgroundColor: "#FFFFFF",
        direction: isArabic ? "rtl" : "ltr",
      }}
    >
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ textAlign: "center", mb: { xs: 4, md: 6 } }}>
          <Typography
            sx={{
              fontSize: { xs: "10px", sm: "11px" },
              fontWeight: 600,
              color: SAGE_PINK,
              letterSpacing: "1.5px",
              textTransform: "uppercase",
              fontFamily: "var(--font-outfit)",
              mb: { xs: 1.5, sm: 2 },
            }}
          >
            {t("sageLoop.label")}
          </Typography>

          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: "26px", sm: "30px", md: "34px", lg: "38px" },
              fontWeight: 700,
              lineHeight: 1.25,
              color: "#14122B",
              fontFamily: "var(--font-plusjakarta)",
              mb: { xs: 2, sm: 2.5 },
            }}
          >
            {t("sageLoop.heading")}
          </Typography>

          <Typography
            sx={{
              fontSize: { xs: "14px", sm: "15px", md: "16px" },
              color: "#55556E",
              lineHeight: 1.8,
              fontFamily: "var(--font-outfit)",
              maxWidth: 750,
              mx: "auto",
            }}
          >
            {t("sageLoop.description")}
          </Typography>
        </Box>

        {/* Desktop loop: 01 → 02 ↓ 03 ← 04 ↑ 01 */}
        {steps.length >= 4 && (
          <Box
            sx={{
              display: { xs: "none", md: "grid" },
              gridTemplateColumns: "1fr 48px 1fr",
              gridTemplateRows: "auto 40px auto",
              maxWidth: 720,
              mx: "auto",
              mb: 6,
            }}
          >
            {/* Row 1 */}
            <StepCard number={num(0)} title={steps[0]} isArabic={isArabic} />
            <Center>
              <ArrowForwardRoundedIcon sx={{ ...arrowSx, ...flip }} />
            </Center>
            <StepCard number={num(1)} title={steps[1]} isArabic={isArabic} />

            {/* Row 2 (vertical arrows) */}
            <Center>
              <ArrowUpwardRoundedIcon sx={arrowSx} />
            </Center>
            <Box />
            <Center>
              <ArrowDownwardRoundedIcon sx={arrowSx} />
            </Center>

            {/* Row 3 (reversed: 04 on start side, 03 on end side) */}
            <StepCard number={num(3)} title={steps[3]} variant="pink" isArabic={isArabic} />
            <Center>
              <ArrowBackRoundedIcon sx={{ ...arrowSx, ...flip }} />
            </Center>
            <StepCard number={num(2)} title={steps[2]} variant="dark" isArabic={isArabic} />
          </Box>
        )}

        {/* Mobile flow */}
        <Box
          sx={{
            display: { xs: "flex", md: "none" },
            flexDirection: "column",
            gap: 1,
            mb: 4,
          }}
        >
          {steps.map((title, i) => (
            <React.Fragment key={i}>
              <StepCard number={num(i)} title={title} variant={variantFor(i)} isArabic={isArabic} />
              {i < steps.length - 1 && (
                <Center>
                  <ArrowDownwardRoundedIcon sx={arrowSx} />
                </Center>
              )}
            </React.Fragment>
          ))}
        </Box>

        {/* CTA */}
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <NextLink href="/free-trial-of-ai-recruiting-tool" passHref legacyBehavior>
            <Button
              component="a"
              sx={{
                bgcolor: "#000",
                color: "white",
                px: { xs: 3, sm: 3.5 },
                py: { xs: 1.2, sm: 1.4 },
                borderRadius: "50px",
                textTransform: "none",
                fontSize: { xs: "14px", sm: "15px" },
                fontWeight: 500,
                fontFamily: "var(--font-outfit)",
                whiteSpace: "nowrap",
                transition: "all 0.2s ease-in-out",
                "&:hover": { bgcolor: "#E0457B", transform: "scale(1.03)" },
              }}
            >
              {t("sageLoop.cta")}
            </Button>
          </NextLink>
        </Box>
      </Container>
    </Box>
  );
};

export default SageLoopSection;